import React, { useEffect, useState } from 'react';
import { UserRole } from '../../user/user.type';
import { PTestQuestion } from '../onboarding.service';
import { PTestAnswerType } from '../../user/user.type';
import logo from '../../media/logo.png';
import classNames from 'classnames';
import OnboardingStepPTestUi from './screens/onboarding-step-p-test.ui';

import styles from './p-test-steps.module.css';

type PTestStepsUiProps = {
  userRole: UserRole;
  returnedUser: boolean;
  progress: number;
  currentPage: number;
  totalPages: number;
  questions: PTestQuestion[];
  answers: PTestAnswerType[];
  loading: boolean;
  error: string | null;
  canGoPrev: boolean;
  isContinuable: boolean;
  isLastPage: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onUpdateAnswer: (questionId: string, answer: string) => void;
  onSignOut: () => void;
};

function PTestStepsUi(props: PTestStepsUiProps) {
  const {
    userRole,
    returnedUser,
    progress,
    currentPage,
    totalPages,
    questions,
    answers,
    loading,
    error,
    canGoPrev,
    isContinuable,
    isLastPage,
    onNext,
    onPrevious,
    onUpdateAnswer,
    onSignOut,
  } = props;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        onNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onNext]);

  // Auto-advance to next page if all questions are answered and not on the last page
  useEffect(() => {
    if (isContinuable && questions.length > 0 && !isLastPage) {
      const allQuestionsAnswered = questions.every(question => {
        const answer = answers.find(a => a.questionId === question.id);
        return answer && answer.answer;
      });

      if (allQuestionsAnswered) {
        const timer = setTimeout(() => {
          onNext();
        }, 500);

        return () => clearTimeout(timer);
      }
    }
  }, [isContinuable, questions, answers, isLastPage, onNext]);

  const [dialogOpen, setDialogOpen] = useState(returnedUser);

  const toggleDialog = () => {
    setDialogOpen(prev => !prev);
  };

  return (
    <>
      {returnedUser && dialogOpen && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            {userRole === UserRole.Founder ? (
              <div className={styles.dialogText}>
                Please fill out this personality quiz to assess your compatibility with the tech co-founder.
                Just be yourself and don't overthink it, it shouldn't take more than a minute!
              </div>
            ) : (
              <div className={styles.dialogText}>
                Please fill out this personality quiz to assess your compatibility with the founder.
                Just be yourself and don't overthink it, it shouldn't take more than a minute!
              </div>
            )}
            <button
              className={styles.dialogButton}
              onClick={toggleDialog}
              autoFocus
            >
              Take the test
            </button>
          </div>
        </div>
      )}

      <div className={styles.layout}>
        <button
          type="button"
          className={styles.signOutButton}
          onClick={onSignOut}
        >
          Sign Out
        </button>
        <div className={styles.logoWrapper}>
          <img
            src={logo}
            alt="Foundermatcha Logo"
            className={styles.logo}
          />
        </div>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.title}>Personality Quiz</div>
            <div
              className={classNames(styles.progressBar, {
                [styles.hidden]: progress === null,
              })}
            >
              <div
                className={styles.progressBarInner}
                style={{
                  width: `${(progress || 0) * 100}%`,
                }}
              />
            </div>

            <div className={styles.subtitle}>
              Page {currentPage} of {totalPages}
            </div>
          </div>

          <div className={styles.form}>
            <div className={styles.contentWrapper}>
              <div className={styles.page}>
                <OnboardingStepPTestUi
                  testQuestions={questions}
                  testAnswers={answers}
                  loading={loading}
                  error={error}
                  stepErrors={false}
                  onUpdateAnswer={onUpdateAnswer}
                />
              </div>

              <div className={styles.footer}>
                <button
                  className={styles.button}
                  type="button"
                  onClick={onPrevious}
                  disabled={!canGoPrev}
                >
                  Previous
                </button>

                <button
                  className={styles.button}
                  type="button"
                  onClick={onNext}
                  disabled={!isContinuable}
                >
                  {isLastPage ? 'Complete' : 'Next'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PTestStepsUi;
